import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { HiOutlineSquares2X2, HiOutlineCube, HiOutlineMagnifyingGlass } from "react-icons/hi2";
import { useNavigationAccess } from "../navigation/useNavigationAccess";
import { flattenNavForPalette } from "../navigation/flattenNavForPalette";
import { filterLauncherEntriesByQuery } from "../navigation/launcherFilter";
import { MAX_APPS_SEED } from "../data/maxAppsDefinitions";
import "./AppLauncherDropdown.css";

const AppLauncherDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const { ctx, featuresLoading } = useNavigationAccess();

  // Re-read when parent re-renders after tenant switch so the launcher list never keys off a stale ctx alone
  const tenantIdKey =
    typeof window !== "undefined" ? localStorage.getItem("tenantId") : "";

  const modulePaths = useMemo(() => new Set(MAX_APPS_SEED.map(a => a.primaryPath)), []);

  const entries = useMemo(() => {
    // Do not block on featuresLoading: subscribedModules is the full catalog while scope loads,
    // and launcher forces navAppScopeActive off — list can render immediately after login.
    // Bypass app-scoping for the launcher so all apps are always visible
    const globalCtx = { ...ctx, navAppScopeActive: false };
    const allNav = flattenNavForPalette(globalCtx);
    
    // Known keys for module entry points (Hubs/Dashboards)
    const hubKeys = new Set([
      "pm-hub", "MACHINE_HEALTH", "oee-dashboard", "wp-dashboard", "wp-list",
      "rca-records", "capa-records", "spare-parts", "fives", "KAIZEN", "QUALITY_5S",
      "kaizen", "opl", "OPL", "incident-analytics", "hazard-analytics", "HAZARD",
      "bi-dashboard", "reports-hub", "REPORTS", "work-order", "WORK_ORDER", "flowchart", "FLOWCHART"
    ]);

    // Track which modules we've already added to avoid duplicates
    const seenModules = new Set();
    const result = [];

    // Map paths to module codes for deduplication
    const pathToCode = new Map(MAX_APPS_SEED.map(a => [a.primaryPath, a.code]));
    const keyToCode = new Map();
    MAX_APPS_SEED.forEach(app => {
      app.navKeys?.forEach(k => keyToCode.set(k, app.code));
    });

    allNav.forEach(item => {
      const codeFromPath = pathToCode.get(item.path);
      const codeFromKey = keyToCode.get(item.id);
      const code = codeFromPath || codeFromKey;

      if (code) {
        if (!seenModules.has(code)) {
          seenModules.add(code);
          result.push(item);
        }
      } else if (hubKeys.has(item.id) || modulePaths.has(item.path)) {
        // Fallback for items that look like hubs but aren't strictly mapped in seed
        const fallbackCode = item.id || item.path;
        if (!seenModules.has(fallbackCode)) {
          seenModules.add(fallbackCode);
          result.push(item);
        }
      }
    });

    // Sort alphabetically by display name before returning
    return result.sort((a, b) => {
      const getLabel = (item) => {
        const seedApp = MAX_APPS_SEED.find(sa => {
          const pathToCode = sa.primaryPath === item.path;
          const keyToCode = sa.navKeys?.includes(item.id);
          return pathToCode || keyToCode || sa.code === item.id;
        });
        return (seedApp ? seedApp.name : item.label || "").toLowerCase();
      };
      return getLabel(a).localeCompare(getLabel(b));
    });
  }, [ctx, tenantIdKey]);

  const filteredEntries = useMemo(
    () => filterLauncherEntriesByQuery(entries, searchQuery, { includeGroup: false }),
    [entries, searchQuery],
  );

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      return;
    }
    const t = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEsc = (event) => {
      if (event.key !== "Escape") return;
      if (searchQuery.trim()) {
        event.preventDefault();
        setSearchQuery("");
        return;
      }
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEsc);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [isOpen, searchQuery]);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  return (
    <div className="app-launcher-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className={`shell-apps-link ${isOpen ? "is-active" : ""}`}
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        title="App Launcher"
      >
        <HiOutlineSquares2X2 aria-hidden />
        <span className="shell-apps-label">Apps</span>
      </button>

      {isOpen && (
        <div className="al-panel">
          <div className="al-panel-top">
            <div className="al-header">
              <h4>Apps & Modules</h4>
              <Link 
                to="/home" 
                className="al-explore"
                onClick={() => setIsOpen(false)}
              >
                Go to Home
              </Link>
            </div>
            <label className="al-search">
              <HiOutlineMagnifyingGlass className="al-search-icon" aria-hidden />
              <input
                ref={searchInputRef}
                type="search"
                className="al-search-input"
                placeholder="Search apps…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search apps and modules"
                autoComplete="off"
                spellCheck="false"
              />
            </label>
          </div>
          
          <div className="al-content">
            {filteredEntries.length === 0 && !featuresLoading ? (
              <div className="al-empty">
                {searchQuery.trim() ? "No matching apps." : "No apps available."}
              </div>
            ) : (
              <div className="al-list">
                {filteredEntries.map((item) => {
                  const Icon = item.icon || HiOutlineCube;
                  const seedApp = MAX_APPS_SEED.find(a => {
                    const pathToCode = a.primaryPath === item.path;
                    const keyToCode = a.navKeys?.includes(item.id);
                    return pathToCode || keyToCode || a.code === item.id;
                  });
                  const displayName = seedApp ? seedApp.name : item.label;
                  const navigationTarget = seedApp ? seedApp.primaryPath : item.path;

                  return (
                    <Link
                      key={item.id}
                      to={navigationTarget}
                      className="al-row"
                      onClick={() => setIsOpen(false)}
                    >
                      <div className="al-row-icon">
                        <Icon />
                      </div>
                      <span className="al-row-label">{displayName}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppLauncherDropdown;
